/**
 * MetricSparkline — compact inline sparkline used inside KPI cards and
 * any other tight layout where a full chart would overwhelm the
 * surrounding chrome.
 *
 * The sparkline renders a single token-driven series with no axes or
 * grid. It is intentionally axis-less so it composes inside table cells,
 * metric strips, and the SecuritySurfacePanel metric row.
 *
 * Accessibility: the component sets `role="img"` and renders an
 * `aria-label` constructed from the supplied series name. The visible
 * trend remains the canonical signal — callers should keep the
 * underlying numeric value alongside the sparkline.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { Line, LineChart, ResponsiveContainer, type LineProps } from "recharts";
import { DEFAULT_CHART_PALETTE, type ChartPalette, theoremSeriesColor } from "./ChartTheme.js";

export interface MetricSparklineProps {
  /** Ordered series values; the array index drives the X position. */
  readonly values: readonly number[];
  /** Pixel height. Defaults to 36 for a tight metric strip. */
  readonly height?: number;
  /**
   * Optional explicit colour. When omitted the sparkline resolves a
   * theorem-keyed colour via {@link theoremSeriesColor}, then falls
   * back to the palette's primary.
   */
  readonly color?: string;
  /** Optional canonical theorem name for token lookup. */
  readonly theorem?: string;
  /** Curve type — defaults to `monotone` so the line stays readable. */
  readonly curve?: LineProps["type"];
  /** Accessible label announced by assistive tech. */
  readonly ariaLabel: string;
  /** Palette override; falls back to {@link DEFAULT_CHART_PALETTE}. */
  readonly palette?: ChartPalette;
}

interface SparkPoint {
  readonly step: number;
  readonly value: number;
}

const DEFAULT_RESPONSIVE_WIDTH = 160;

function buildPoints(values: readonly number[]): readonly SparkPoint[] {
  return values.map((value, step) => ({ step, value }));
}

function resolveStroke(
  color: string | undefined,
  theorem: string | undefined,
  palette: ChartPalette,
): string {
  if (color !== undefined) return color;
  if (theorem !== undefined) return theoremSeriesColor(theorem, palette);
  return palette.primary;
}

export const MetricSparkline = React.memo(function MetricSparkline({
  values,
  height = 36,
  color,
  theorem,
  curve = "monotone",
  ariaLabel,
  palette = DEFAULT_CHART_PALETTE,
}: MetricSparklineProps): React.JSX.Element {
  const data = React.useMemo(() => buildPoints(values), [values]);
  const stroke = resolveStroke(color, theorem, palette);

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-csk-chart="metric-sparkline"
      style={{ width: "100%", height }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        minHeight={height}
        initialDimension={{ width: DEFAULT_RESPONSIVE_WIDTH, height }}
      >
        <LineChart data={[...data]} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Line
            type={curve}
            dataKey="value"
            stroke={stroke}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});
